import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { UseAuthContext } from '../../context/AuthContext';
import { validateSearch, getErrorMsg } from '../utils/commonUtil';
import AddressMap from './AddressMap';
//import json from './radius.json'

export default function Search({ setResultData, setSearchSubmit, setCheckboxSelected }) {
    const { t } = useTranslation();
    const { token } = UseAuthContext();
    const suggestionInputRef = useRef(null);
    const latestAutocompleteRequestRef = useRef(0);
    const [load, setLoad] = useState(false);
    const [validList, setValidList] = useState([]);
    const [searchParams, setSearchParams] = useState({
        postCode: "", //5
        streetBuildingId: "", //2
        municipalityName: "", //8
        streetName: "", //1
        districtName: "", //6
        districtSubdivisionIdentifier: "", // 7
        isOfficial: "", //9
        floorId: "", //3
        suiteId: "", //4
    });
    const [suggestion, setSuggestion] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const [pageInfo, setPageInfo] = useState({
        page: 0,
        size: "5"
    });

    const handleInput = (e) => {
        let id = e.target.id;
        let value = e.target.value;
        setSelectedAddress(null);
        setSearchParams(pre => ({ ...pre, [id]: value }));
    }

    useEffect(() => {
        setValidList(validateSearch(searchParams))
    }, [searchParams])

    const verifyValid = (name) => {
        return validList.includes(name) ? <span>*</span> : null;
    }

    const resetAll = () => {
        setSearchParams({
            postCode: "", //5
            streetBuildingId: "", //2
            municipalityName: "", //8
            streetName: "", //1
            districtName: "", //6
            districtSubdivisionIdentifier: "", // 7
            isOfficial: "", //9
            floorId: "", //3
            suiteId: "", //4
        });
        setPageInfo({
            page: 0,
            size: "5"
        })
        setResultData({ data: null, loading: false, msg: "" });
        setSuggestion('');
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        setSelectedAddress(null);
        latestAutocompleteRequestRef.current += 1;
    }

    const searchHandler = (pageNo) => {
        setLoad(true);
        setResultData({ data: null, loading: true, msg: t('result.loading') });
        axios.post(`/api/v2/addresses/legacy?page=${pageNo}&size=${pageInfo.size}`,
            {
                ...searchParams, floorId: searchParams.floorId ? searchParams.floorId : null,
                suiteId: searchParams.suiteId ? searchParams.suiteId : null
            }, { headers: { "Authorization": `Bearer ${token}` } })
            .then(res => {
                if (res.data.content.length === 0) {
                    setResultData({ data: null, loading: false, msg: t('result.no_data') });
                } else {
                    setResultData({ data: res.data, loading: false, msg: "" });
                }
                setLoad(false);
            })
            .catch(err => {
                setResultData({ data: null, loading: false, msg: getErrorMsg(err.message) });
                setLoad(false);
            })
    }

    const fetchAutocompleteSuggestions = (query) => {
        if (query && query.length > 3) {
            const requestId = latestAutocompleteRequestRef.current + 1;
            latestAutocompleteRequestRef.current = requestId;
            axios.get(`/api/v2/address/autocomplete?q=${encodeURIComponent(query)}&showCaretPosition=true&numberOfSuggestions=50`, { headers: { "Authorization": `Bearer ${token}` } })
                .then(res => {
                    if (requestId !== latestAutocompleteRequestRef.current) {
                        return;
                    }
                    if (res.data && res.data.length > 0) {
                        setSuggestions(res.data.map(d => ({
                            data: d.data || {},
                            value: d.text || '',
                            caretPosition: d.caretPosition
                        })).filter(d => d.value));
                        setShowSuggestions(true);
                        setHighlightedIndex(-1);
                    } else {
                        setSuggestions([]);
                        setShowSuggestions(false);
                        setHighlightedIndex(-1);
                    }
                })
                .catch(err => {
                    if (requestId !== latestAutocompleteRequestRef.current) {
                        return;
                    }
                    console.error(err);
                    setSuggestions([]);
                    setShowSuggestions(false);
                    setHighlightedIndex(-1);
                })
        } else {
            latestAutocompleteRequestRef.current += 1;
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }
    }

    const getBuildingNumber = (buildingNoWithSuffix) => {
        return buildingNoWithSuffix?.split(',')[0]?.trim() || '';
    }

    const registerAllFields = (selectedSuggestion) => {
        const selectedAddress = selectedSuggestion?.data;

        if (selectedAddress?.id) {
            setSearchParams(pre => ({
                ...pre,
                streetName: selectedAddress.streetName || '',
                streetBuildingId: getBuildingNumber(selectedAddress.buildingNoWithSuffix),
                postCode: selectedAddress.postalCode || '',
                municipalityName: selectedAddress.municipalityName || '',
                districtName: selectedAddress.city || '',
                floorId: selectedAddress.floorId || selectedAddress.floor || '',
                suiteId: selectedAddress.suiteId || selectedAddress.suite || selectedAddress.door || ''
            }));
            setShowSuggestions(false);
            return;
        }

        if (Number.isInteger(selectedSuggestion?.caretPosition) && suggestionInputRef.current) {
            window.requestAnimationFrame(() => {
                suggestionInputRef.current.setSelectionRange(
                    selectedSuggestion.caretPosition,
                    selectedSuggestion.caretPosition
                );
            });
        }

        return selectedSuggestion;
    }

    const handleSuggestion = (val) => {
        setSelectedAddress(null);
        setSuggestion(val);
        fetchAutocompleteSuggestions(val);
    }

    const handleSuggestionSelect = (selectedSuggestion) => {
        latestAutocompleteRequestRef.current += 1;
        setSelectedAddress(selectedSuggestion.data?.id ? selectedSuggestion : null);
        setSuggestion(selectedSuggestion.value);
        registerAllFields(selectedSuggestion);
        setHighlightedIndex(-1);

        if (!selectedSuggestion.data?.id) {
            fetchAutocompleteSuggestions(selectedSuggestion.value);
        }
    }

    const handleSuggestionKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                e.preventDefault();
                handleSuggestionSelect(suggestions[highlightedIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }
    }
    const handleCheckboxSelection = (e)=> {
        console.log('e.target.value=',e.target.checked)
        setCheckboxSelected(e.target.checked)
    }

    return (
        <div className="mt-2 text-sm">
            <div className="form-row">
                <div className='form-group col-md-12 position-relative'>
                    <label htmlFor='suggestion'>Search For Suggested Address</label>
                    <input ref={suggestionInputRef} className='form-control form-control-sm' name="suggestion" id="suggestion"
                        value={suggestion} onChange={(e) => handleSuggestion(e.target.value)} onFocus={() => setShowSuggestions(suggestions.length > 0)} onKeyDown={handleSuggestionKeyDown} autoComplete="off" />

                    {showSuggestions && suggestions.length > 0 && (
                        <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1000, maxHeight: "16rem", overflowY: "auto" }}>
                            {suggestions.map((s, i) => (
                                <button
                                    type="button"
                                    key={`${s.value}-${i}`}
                                    className={`list-group-item list-group-item-action py-1${i === highlightedIndex ? ' active' : ''}`}
                                    onMouseEnter={() => setHighlightedIndex(i)}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSuggestionSelect(s);
                                    }}
                                >
                                    {s.value}
                                </button>
                            ))}
                        </div>
                    )}

                </div>
                <div className="form-group col-md">
                    <label htmlFor="streetName">{t('search.street_name')}{verifyValid("streetName")}</label>
                    <input type="text" className="form-control form-control-sm" id="streetName" maxLength="40"
                        value={searchParams.streetName} onChange={handleInput} />
                </div>
                <div className="form-group col-md">
                    <label htmlFor="streetBuildingId">{t('search.building_id')}{verifyValid("streetBuildingId")}</label>
                    <input type="text" className="form-control form-control-sm" id="streetBuildingId" maxLength="4"
                        value={searchParams.streetBuildingId} onChange={handleInput} />
                </div>
                <div className="form-group col-md">
                    <label htmlFor="floor">{t('search.floor')}{verifyValid("floorId")}</label>
                    <input type="text" className="form-control form-control-sm" id="floorId" maxLength="2"
                        value={searchParams.floorId} onChange={handleInput} />
                </div>
                <div className="form-group col-md">
                    <label htmlFor="suite">{t('search.suite')}{verifyValid("suiteId")}</label>
                    <input type="text" className="form-control form-control-sm" id="suiteId" maxLength="4"
                        value={searchParams.suiteId} onChange={handleInput} />
                </div>
                <div className="form-group col-md">
                    <label htmlFor="post">{t('search.post_code')}{verifyValid("postCode")}</label>
                    <input type="text" className="form-control form-control-sm" id="postCode" maxLength="4"
                        value={searchParams.postCode} onChange={handleInput} />
                </div>

            </div>
            <div className="form-row">

                <div className="form-group col-md">
                    <label htmlFor="district">{t('search.district')}{verifyValid("districtName")}</label>
                    <input type="text" className="form-control form-control-sm" id="districtName" maxLength="40"
                        value={searchParams.districtName} onChange={handleInput} />
                </div>

                <div className="form-group col-md">
                    <label htmlFor="districtSubdivisionIdentifier">{t('search.district_subdivision')}{verifyValid("districtSubdivisionIdentifier")}</label>
                    <input type="text" className="form-control form-control-sm" id="districtSubdivisionIdentifier" maxLength="50"
                        value={searchParams.districtSubdivisionIdentifier} onChange={handleInput} />
                </div>

                <div className="form-group col-md">
                    <label htmlFor="municipalityName">{t('search.municipality_name')}{verifyValid("municipalityName")}</label>
                    <input type="text" className="form-control form-control-sm" id="municipalityName" maxLength="40"
                        value={searchParams.municipalityName} onChange={handleInput} />
                </div>

                <div className="form-group col-md">
                    <label htmlFor="isOfficial">{t('search.isOfficial')}</label>
                    <select id="isOfficial" className="form-control form-control-sm"
                        value={searchParams.isOfficial} onChange={handleInput}>
                        <option value="" defaultValue>All</option>
                        <option value="0" >Official</option>
                        <option value="1" >TdcNet Unofficial</option>
                        <option value="8" >Nuuday Unofficial</option>
                    </select>
                </div>
                <div className="form-group col-md">
                    <label htmlFor="size">{t('search.records')}</label>
                    <select id="size" className="form-control form-control-sm"
                        value={pageInfo.size} onChange={(e) => setPageInfo(pre => ({ page: 0, size: e.target.value }))}>
                        <option value="5" >5</option>
                        <option value="10" >10</option>
                        <option value="15" >15</option>
                        <option value="20" >20</option>
                        <option value="25" >25</option>
                        <option value="50" >50</option>
                    </select>
                </div>

            </div>
            {selectedAddress && <AddressMap address={selectedAddress} />}
            <div  className="custom-control  custom-control-inline">
                <input class="form-check-input" type="checkbox" value=""
                 id="defaultCheck1" onChange={handleCheckboxSelection}/>
                <label class="form-check-label" for="defaultCheck1">
                    Display results with access address details
                </label>
            </div>
            <div className="row gy-1">
                <div className="col-6 text-right">
                    <button className="btn btn-primary"
                        disabled={load || validList.length === 0 || validList.some(v => searchParams[v].trim().length === 0)}
                        onClick={() => { searchHandler(0); setSearchSubmit(() => searchHandler) }}>{load ? t('search.wait') : t('search.search')}</button>
                </div>
                <div className="col-6 text-left">
                    <button className="btn btn-secondary ml-1" onClick={() => resetAll()}>{t('search.reset')}</button>
                </div>
            </div>

        </div>
    )
}
